"use client"
import type React from "react"
import { useEffect } from "react"
import { Modal, Form, Input, Select, Switch, Button, Space, Card, DatePicker, TimePicker, Alert } from "antd"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import type { Trigger } from "../../../hooks/api/notification/notificationService"
import dayjs from "dayjs"

const { Option } = Select
const { TextArea } = Input

interface TriggerFormModalProps {
  isDialogOpen: boolean
  editingTrigger: Trigger | null
  handleSubmit: (formData: any) => void
  handleCloseDialog: () => void
  loading?: boolean
}

const triggerTypeValidationConfig: {
  [key: string]: {
    label: string
    placeholder: string
    description: string
  }
} = {
  inactivity_alert: {
    label: "Days of Inactivity",
    placeholder: "e.g., 7 (days)",
    description: "Number of days without activity before triggering",
  },
  subscription_expiry: {
    label: "Days Before Expiry",
    placeholder: "e.g., 3 (days)",
    description: "Number of days before subscription expires",
  },
  low_wallet_balance: {
    label: "Minimum Balance",
    placeholder: "e.g., 100",
    description: "Wallet balance threshold to trigger alert",
  },
  achievement: {
    label: "Achievement ID",
    placeholder: "e.g. 0",
    description: "Identifier for the achievement to track",
  },
  custom: {
    label: "Custom Criteria",
    placeholder: "e.g., 0",
    description: "Custom criteria value for this trigger",
  },
  weekly_new_subscribers: {
    label: "Minimum Count",
    placeholder: "e.g., 10 (subscribers)",
    description: "Minimum number of new weekly subscribers to trigger",
  },
  monthly_new_subscribers: {
    label: "Minimum Count",
    placeholder: "e.g., 50 (subscribers)",
    description: "Minimum number of new monthly subscribers to trigger",
  },
  daily_renewed_subscribers: {
    label: "Minimum Count",
    placeholder: "e.g., 5 (renewals)",
    description: "Minimum number of daily renewals to trigger",
  },
  weekly_renewed_subscribers: {
    label: "Minimum Count",
    placeholder: "e.g., 25 (renewals)",
    description: "Minimum number of weekly renewals to trigger",
  },
  monthly_renewed_subscribers: {
    label: "Minimum Count",
    placeholder: "e.g., 100 (renewals)",
    description: "Minimum number of monthly renewals to trigger",
  },
  unsubscribed_immediately: {
    label: "Threshold Count",
    placeholder: "e.g., 5 (unsubscribes)",
    description: "Number of immediate unsubscribes to trigger alert",
  },
  unsubscribed_5_days: {
    label: "Threshold Count",
    placeholder: "e.g., 10 (unsubscribes)",
    description: "Number of 5-day unsubscribes to trigger alert",
  },
  unsubscribed_after_expiry: {
    label: "Threshold Count",
    placeholder: "e.g., 15 (unsubscribes)",
    description: "Number of post-expiry unsubscribes to trigger alert",
  },
  expiry_3_days: {
    label: "Threshold Count",
    placeholder: "e.g., 20 (expiring)",
    description: "Number of subscriptions expiring in 3 days to trigger",
  },
  expiry_tomorrow: {
    label: "Threshold Count",
    placeholder: "e.g., 10 (expiring)",
    description: "Number of subscriptions expiring tomorrow to trigger",
  },
  expired_without_renewal: {
    label: "Threshold Count",
    placeholder: "e.g., 25 (expired)",
    description: "Number of expired without renewal to trigger",
  },
  active_7_days: {
    label: "Threshold Count",
    placeholder: "e.g., 30 (active users)",
    description: "Number of 7-day active users to trigger",
  },
  active_3_days: {
    label: "Threshold Count",
    placeholder: "e.g., 15 (active users)",
    description: "Number of 3-day active users to trigger",
  },
  active_yesterday: {
    label: "Threshold Count",
    placeholder: "e.g., 5 (active users)",
    description: "Number of yesterday active users to trigger",
  },
  inactive_7_days: {
    label: "Threshold Count",
    placeholder: "e.g., 50 (inactive users)",
    description: "Number of 7-day inactive users to trigger",
  },
  inactive_2_weeks: {
    label: "Threshold Count",
    placeholder: "e.g., 75 (inactive users)",
    description: "Number of 2-week inactive users to trigger",
  },
  inactive_1_month: {
    label: "Threshold Count",
    placeholder: "e.g., 100 (inactive users)",
    description: "Number of 1-month inactive users to trigger",
  },
}

const TriggerFormModal: React.FC<TriggerFormModalProps> = ({
  isDialogOpen,
  editingTrigger,
  handleSubmit,
  handleCloseDialog,
  loading = false,
}) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (editingTrigger && isDialogOpen) {
      const formValues = {
        name: editingTrigger.name,
        trigger_type: editingTrigger.trigger_type,
        criteria_value: editingTrigger.criteria_value,
        description: editingTrigger.description,
        actions: editingTrigger.actions,
        is_active: editingTrigger.is_active,
        schedule_date: editingTrigger.schedule_date ? dayjs(editingTrigger.schedule_date) : null,
        schedule_time: editingTrigger.schedule_time ? dayjs(editingTrigger.schedule_time, "HH:mm") : dayjs("06:00", "HH:mm"),
      }
      // console.log("🔵 EDITING TRIGGER - Form Values:", formValues)
      // console.log("🔵 EDITING TRIGGER - Original Data:", editingTrigger)
      form.setFieldsValue(formValues)
    } else if (isDialogOpen) {
      form.resetFields()
      // Set default values for new trigger
      const defaultValues = {
        actions: [{ type: "send_sms", channel: "sms" }],
        is_active: true,
        schedule_time: dayjs("06:00", "HH:mm"),
      }
      // console.log("🟢 CREATE NEW TRIGGER - Default Values:", defaultValues)
      form.setFieldsValue(defaultValues)
    }
  }, [editingTrigger, form, isDialogOpen])

  const onFinish = (values: any) => {
    // console.log("📝 FORM SUBMITTED - Raw Values:", values)
    
    // Format schedule_date to ISO string if it exists
    const formattedScheduleDate = values.schedule_date ? values.schedule_date.toISOString() : null
    
    // Format schedule_time to HH:mm format if it exists
    const formattedScheduleTime = values.schedule_time ? values.schedule_time.format("HH:mm") : null

    // Handle criteria_value for trigger types that don't require it
    const requiresCriteria = ['inactivity_alert', 'subscription_expiry', 'low_wallet_balance', 'achievement', 'custom'].includes(values.trigger_type)
    
    const finalValues = {
      ...values,
      schedule_date: formattedScheduleDate,
      schedule_time: formattedScheduleTime,
      // Send default criteria_value for triggers that don't require it
      criteria_value: requiresCriteria ? values.criteria_value : "0"
    }

    // console.log("🚀 FINAL PAYLOAD - Submitting:", finalValues)
    // console.log("📌 Is Editing?:", !!editingTrigger)
    
    handleSubmit(finalValues)
  }

  const onCancel = () => {
    handleCloseDialog()
    form.resetFields()
  }

  const triggerTypes = [
    { value: "inactivity_alert", label: "Inactivity Alert" },
    { value: "subscription_expiry", label: "Subscription Expiry" },
    { value: "low_wallet_balance", label: "Low Wallet Balance" },
    { value: "achievement", label: "Achievement" },
    { value: "custom", label: "Custom" },
    { value: "daily_new_subscribers", label: "Daily New Subscribers" },
    { value: "weekly_new_subscribers", label: "Weekly New Subscribers" },
    { value: "monthly_new_subscribers", label: "Monthly New Subscribers" },
    { value: "daily_renewed_subscribers", label: "Daily Renewed Subscribers" },
    { value: "weekly_renewed_subscribers", label: "Weekly Renewed Subscribers" },
    { value: "monthly_renewed_subscribers", label: "Monthly Renewed Subscribers" },
    { value: "unsubscribed_immediately", label: "Unsubscribed Immediately" },
    { value: "unsubscribed_5_days", label: "Unsubscribed 5 Days" },
    { value: "unsubscribed_after_expiry", label: "Unsubscribed After Expiry" },
    { value: "expiry_3_days", label: "Expiry 3 Days" },
    { value: "expiry_tomorrow", label: "Expiry Tomorrow" },
    { value: "expired_without_renewal", label: "Expired Without Renewal" },
    { value: "active_7_days", label: "Active 7 Days" },
    { value: "active_3_days", label: "Active 3 Days" },
    { value: "active_yesterday", label: "Active Yesterday" },
    { value: "inactive_7_days", label: "Inactive 7 Days" },
    { value: "inactive_2_weeks", label: "Inactive 2 Weeks" },
    { value: "inactive_1_month", label: "Inactive 1 Month" },
    { value: "daily_new_subscription", label: "Daily New Subscription" },
    { value: "weekly_new_subscription", label: "Weekly New Subscription" },
  ]

  const actionTypes = [
    { value: "send_sms", label: "Send SMS" },
    // { value: 'send_whatsapp', label: 'Send WhatsApp' },
    { value: "add_bonus", label: "Add Bonus" },
  ]

  const bonusTypes = [
    { value: "lives", label: "Lives" },
    { value: "credits", label: "Credits" },
    { value: "points", label: "Points" },
  ]

  return (
    <Modal
      title={editingTrigger ? "Edit Trigger" : "Create New Trigger"}
      open={isDialogOpen}
      onCancel={onCancel}
      footer={null}
      width={700}
      style={{ top: 20 }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          is_active: true,
          schedule_time: dayjs("06:00", "HH:mm"),
          actions: [{ type: "send_sms", channel: "sms" }],
        }}
      >
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Form.Item
            name="name"
            label="Trigger Name"
            rules={[{ required: true, message: "Please enter trigger name" }]}
          >
            <Input placeholder="e.g., Inactivity Alert" />
          </Form.Item>

          <Form.Item
            name="trigger_type"
            label="Trigger Type"
            rules={[{ required: true, message: "Please select trigger type" }]}
          >
            <Select placeholder="Select trigger type">
              {triggerTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.trigger_type !== curValues.trigger_type}>
          {() => {
            const selectedType = form.getFieldValue("trigger_type")
            const config = selectedType ? triggerTypeValidationConfig[selectedType] : null
            
            // Only show criteria_value field for these 5 trigger types
            const requiresCriteria = ['inactivity_alert', 'subscription_expiry', 'low_wallet_balance', 'achievement', 'custom'].includes(selectedType)

            return (
              <>
                {config && requiresCriteria && (
                  <Alert
                    message={`${config.label} Required`}
                    description={config.description}
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                )}
                {requiresCriteria && (
                  <Form.Item
                    name="criteria_value"
                    label={config?.label || "Criteria Value"}
                    rules={[
                      { required: true, message: "Please enter criteria value" },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve()

                        // Validate based on trigger type
                        switch (selectedType) {
                          case "inactivity_alert":
                          case "subscription_expiry":
                          case "low_wallet_balance":
                            if (!/^\d+$/.test(value)) {
                              return Promise.reject(new Error("Must be a number"))
                            }
                            // No minimum validation - removed the > 0 check
                            break
                          case "achievement":
                          case "custom":
                            if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
                              return Promise.reject(
                                new Error("Must contain only letters, numbers, underscores, or hyphens"),
                              )
                            }
                            break
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                    <Input placeholder={config?.placeholder || "Enter criteria value"} />
                  </Form.Item>
                )}
              </>
            )
          }}
        </Form.Item>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <Form.Item name="schedule_date" label="Schedule Date (Optional)">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="schedule_time"
            label="Trigger Time"
            rules={[{ required: true, message: "Please select time" }]}
          >
            <TimePicker format="HH:mm a" use12Hours style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Please enter description" }]}
        >
          <TextArea placeholder="Describe what this trigger does..." rows={3} />
        </Form.Item>

        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.List name="actions">
          {(fields, { add, remove }) => (
            <Card
              title="Actions"
              size="small"
              extra={
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Add Action
                </Button>
              }
            >
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="start">
                  <Form.Item
                    {...restField}
                    name={[name, "type"]}
                    rules={[{ required: true, message: "Select action type" }]}
                  >
                    <Select placeholder="Action type" style={{ width: 150 }}>
                      {actionTypes.map((action) => (
                        <Option key={action.value} value={action.value}>
                          {action.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues.actions?.[name]?.type !== curValues.actions?.[name]?.type
                    }
                  >
                    {() => {
                      const actionType = form.getFieldValue(["actions", name, "type"])

                      if (actionType === "send_sms") {
                        // || actionType === 'send_whatsapp'
                        return (
                          <>
                            <Form.Item
                              {...restField}
                              name={[name, "channel"]}
                              initialValue={actionType === "send_sms" ? "sms" : "whatsapp"}
                            >
                              <Select style={{ width: 120 }} disabled>
                                <Option value="sms">SMS</Option>
                                {/* <Option value="whatsapp">WhatsApp</Option> */}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "template"]}
                              rules={[{ required: true, message: "Enter message template" }]}
                            >
                              <Input placeholder="Message template" style={{ width: 200 }} />
                            </Form.Item>
                          </>
                        )
                      }

                      if (actionType === "add_bonus") {
                        return (
                          <>
                            <Form.Item
                              {...restField}
                              name={[name, "bonus_type"]}
                              rules={[{ required: true, message: "Select bonus type" }]}
                            >
                              <Select placeholder="Bonus type" style={{ width: 120 }}>
                                {bonusTypes.map((bonus) => (
                                  <Option key={bonus.value} value={bonus.value}>
                                    {bonus.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "count"]}
                              rules={[{ required: true, message: "Enter count" }]}
                            >
                              <Input type="number" placeholder="Count" style={{ width: 100 }} min={1} />
                            </Form.Item>
                          </>
                        )
                      }

                      return null
                    }}
                  </Form.Item>

                  {fields.length > 1 && (
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  )}
                </Space>
              ))}
            </Card>
          )}
        </Form.List>

        <Form.Item className="mb-0 mt-4">
          <div className="flex justify-end gap-2">
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingTrigger ? "Update Trigger" : "Create Trigger"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TriggerFormModal
